import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, Flex, Heading, Tooltip, IconInfoLine, ToggleDetails, Button, IconCopyLine } from '@smarterservices/smarter-ui';
import DraggableModal from '@ui/shared/components/Common/DraggableModal/DraggableModal';
import { useAppStore } from '../../../stores/StoreContext';
import { ConnectionState, ConnectionQuality, VideoQuality } from 'livekit-client';

const SessionDiagnostics = ({ isOpen, onClose }) => {
  const room = useAppStore((state) => state.room);
  const isRoomConnected = useAppStore((state) => state.isRoomConnected);
  const attachedToProctorRoomSid = useAppStore((state) => state.attachedToProctorRoomSid);
  const roomId = useAppStore((state) => state.roomId);
  const user = useAppStore((state) => state.user);

  const [diagnostics, setDiagnostics] = useState({
    room: null,
    participants: [],
    localParticipant: null,
    tracks: [],
    egresses: [],
    isRecording: false,
    connectionState: null,
    lastUpdated: null
  });

  const [trackStats, setTrackStats] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);

  const collectTrackStats = useCallback(async () => {
    if (!room) return;

    const stats = {};

    const collectStatsForPublication = async (pub, participantIdentity) => {
      if (!pub.track) return null;

      try {
        const rtcStats = await pub.track.getRTCStatsReport();
        if (!rtcStats) return null;

        let inboundStats = null;
        let outboundStats = null;

        rtcStats.forEach((report) => {
          if (report.type === 'inbound-rtp') {
            inboundStats = report;
          } else if (report.type === 'outbound-rtp') {
            outboundStats = report;
          }
        });

        const relevantStats = inboundStats || outboundStats;
        if (!relevantStats) return null;

        return {
          bitrate: relevantStats.bitrate || 0,
          bytesReceived: relevantStats.bytesReceived || 0,
          bytesSent: relevantStats.bytesSent || 0,
          packetsReceived: relevantStats.packetsReceived || 0,
          packetsSent: relevantStats.packetsSent || 0,
          packetsLost: relevantStats.packetsLost || 0,
          framesReceived: relevantStats.framesReceived || 0,
          framesDecoded: relevantStats.framesDecoded || 0,
          framesDropped: relevantStats.framesDropped || 0,
          frameWidth: relevantStats.frameWidth || 0,
          frameHeight: relevantStats.frameHeight || 0,
          framesPerSecond: relevantStats.framesPerSecond || 0,
          jitter: relevantStats.jitter || 0,
          timestamp: relevantStats.timestamp || Date.now()
        };
      } catch (error) {
        console.error('Error collecting track stats:', error);
        return null;
      }
    };

    // Collect stats for local participant tracks
    if (room.localParticipant) {
      for (const pub of room.localParticipant.trackPublications.values()) {
        const trackStats = await collectStatsForPublication(pub, room.localParticipant.identity);
        if (trackStats) {
          stats[pub.trackSid] = trackStats;
        }
      }
    }

    // Collect stats for remote participant tracks
    for (const participant of room.remoteParticipants.values()) {
      for (const pub of participant.trackPublications.values()) {
        const trackStats = await collectStatsForPublication(pub, participant.identity);
        if (trackStats) {
          stats[pub.trackSid] = trackStats;
        }
      }
    }

    setTrackStats(stats);
  }, [room]);

  const collectDiagnostics = useCallback(() => {
    if (!room) {
      setDiagnostics({
        room: null,
        participants: [],
        localParticipant: null,
        tracks: [],
        egresses: [],
        isRecording: false,
        connectionState: null,
        lastUpdated: new Date()
      });
      return;
    }

    const participants = Array.from(room.remoteParticipants.values()).map(participant => ({
      sid: participant.sid,
      identity: participant.identity,
      name: participant.name || participant.identity,
      connectionQuality: participant.connectionQuality,
      isSpeaking: participant.isSpeaking,
      metadata: participant.metadata,
      tracks: Array.from(participant.trackPublications.values()).map(pub => ({
        trackSid: pub.trackSid,
        trackName: pub.trackName,
        source: pub.source,
        kind: pub.kind,
        muted: pub.isMuted,
        subscribed: pub.isSubscribed,
        enabled: pub.isEnabled,
        dimensions: pub.dimensions,
        simulcasted: pub.simulcasted,
        videoQuality: pub.kind === 'video' ? pub.videoQuality : undefined,
        layers: pub.kind === 'video' && pub.trackInfo?.layers
          ? pub.trackInfo.layers.map(l => ({
            quality: l.quality,
            width: l.width,
            height: l.height,
            bitrate: l.bitrate,
          }))
          : []
      }))
    }));

    const localParticipant = room.localParticipant ? {
      sid: room.localParticipant.sid,
      identity: room.localParticipant.identity,
      name: room.localParticipant.name || room.localParticipant.identity,
      metadata: room.localParticipant.metadata,
      tracks: Array.from(room.localParticipant.trackPublications.values()).map(pub => ({
        trackSid: pub.trackSid,
        trackName: pub.trackName,
        source: pub.source,
        kind: pub.kind,
        muted: pub.isMuted,
        enabled: pub.track?.isEnabled,
        dimensions: pub.dimensions
      }))
    } : null;

    const allTracks = [];
    room.remoteParticipants.forEach(participant => {
      participant.trackPublications.forEach(pub => {
        allTracks.push({
          participantIdentity: participant.identity,
          trackSid: pub.trackSid,
          trackName: pub.trackName,
          source: pub.source,
          kind: pub.kind,
          muted: pub.isMuted,
          subscribed: pub.isSubscribed
        });
      });
    });

    setDiagnostics({
      room: {
        name: room.name,
        sid: room.roomInfo?.sid,
        state: room.state,
        metadata: room.metadata,
        numParticipants: room.numParticipants,
        numPublishers: room.numPublishers,
        departureTimeout: room.roomInfo?.departureTimeout,
        emptyTimeout: room.roomInfo?.emptyTimeout,
      },
      participants,
      localParticipant,
      tracks: allTracks,
      egresses: [],
      isRecording: room.isRecording,
      connectionState: room.state,
      lastUpdated: new Date()
    });
  }, [room]);

  useEffect(() => {
    if (!isOpen || !room) return;

    collectDiagnostics();
    collectTrackStats();

    const interval = setInterval(() => {
      collectDiagnostics();
      collectTrackStats();
    }, 2000);

    const handleParticipantConnected = () => {
      collectDiagnostics();
      collectTrackStats();
    };
    const handleParticipantDisconnected = () => {
      collectDiagnostics();
      collectTrackStats();
    };
    const handleTrackPublished = () => {
      collectDiagnostics();
      collectTrackStats();
    };
    const handleTrackUnpublished = () => {
      collectDiagnostics();
      collectTrackStats();
    };
    const handleConnectionQualityChanged = () => collectDiagnostics();
    const handleRecordingStatusChanged = () => collectDiagnostics();

    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);
    room.on('trackPublished', handleTrackPublished);
    room.on('trackUnpublished', handleTrackUnpublished);
    room.on('connectionQualityChanged', handleConnectionQualityChanged);
    room.on('recordingStatusChanged', handleRecordingStatusChanged);

    return () => {
      clearInterval(interval);
      if (room) {
        room.off('participantConnected', handleParticipantConnected);
        room.off('participantDisconnected', handleParticipantDisconnected);
        room.off('trackPublished', handleTrackPublished);
        room.off('trackUnpublished', handleTrackUnpublished);
        room.off('connectionQualityChanged', handleConnectionQualityChanged);
        room.off('recordingStatusChanged', handleRecordingStatusChanged);
      }
    };
  }, [isOpen, room, collectDiagnostics, collectTrackStats]);

  const getConnectionStateColor = (state) => {
    switch (state) {
      case ConnectionState.Connected:
        return '#0B874B'; // green
      case ConnectionState.Connecting:
      case ConnectionState.Reconnecting:
        return '#0770A3'; // blue
      case ConnectionState.Disconnected:
        return '#D01A19'; // red
      default:
        return '#394B59'; // gray
    }
  };

  const getConnectionQualityColor = (quality) => {
    switch (quality) {
      case ConnectionQuality.Excellent:
        return '#0B874B'; // green
      case ConnectionQuality.Good:
        return '#0B874B'; // green
      case ConnectionQuality.Poor:
        return '#C45500'; // orange
      case ConnectionQuality.Lost:
        return '#D01A19'; // red
      default:
        return '#394B59'; // gray
    }
  };

  const formatConnectionState = (state) => {
    const stateMap = {
      [ConnectionState.Connected]: 'Connected',
      [ConnectionState.Connecting]: 'Connecting',
      [ConnectionState.Reconnecting]: 'Reconnecting',
      [ConnectionState.Disconnected]: 'Disconnected'
    };
    return stateMap[state] || 'Unknown';
  };

  const formatConnectionQuality = (quality) => {
    const qualityMap = {
      [ConnectionQuality.Excellent]: 'Excellent',
      [ConnectionQuality.Good]: 'Good',
      [ConnectionQuality.Poor]: 'Poor',
      [ConnectionQuality.Lost]: 'Lost',
      [ConnectionQuality.Unknown]: 'Unknown'
    };
    return qualityMap[quality] || 'Unknown';
  };

  const formatBitrate = (bitrate) => {
    if (!bitrate || bitrate === 0) return 'N/A';
    if (bitrate < 1000) return `${bitrate} bps`;
    if (bitrate < 1000000) return `${(bitrate / 1000).toFixed(2)} Kbps`;
    return `${(bitrate / 1000000).toFixed(2)} Mbps`;
  };

  const formatVideoQuality = (quality) => {
    const qualityMap = {
      [VideoQuality.HIGH]: 'High',
      [VideoQuality.MEDIUM]: 'Medium',
      [VideoQuality.LOW]: 'Low',
      [VideoQuality.OFF]: 'Off'
    };
    return qualityMap[quality] ?? 'Unknown';
  };

  const getVideoQualityColor = (quality) => {
    switch (quality) {
      case VideoQuality.HIGH:
        return '#0B874B'; // green
      case VideoQuality.MEDIUM:
        return '#0770A3'; // blue
      case VideoQuality.LOW:
        return '#C45500'; // orange
      case VideoQuality.OFF:
        return '#D01A19'; // red
      default:
        return '#394B59'; // gray
    }
  };

  const formatPacketLoss = (packetsLost, packetsReceived) => {
    const totalPackets = (packetsReceived || 0) + (packetsLost || 0);
    if (totalPackets === 0) return { count: 0, percent: '0.00' };
    const percent = ((packetsLost || 0) / totalPackets * 100).toFixed(2);
    return { count: packetsLost || 0, percent };
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(2)} MB`;
    return `${(bytes / 1073741824).toFixed(2)} GB`;
  };

  const getParticipantDisplayName = (name) => {
    if (!name) return 'Unknown';

    if (name.startsWith('agent-')) {
      return 'Virtual Agent';
    }
    if (name.startsWith('ES')) {
      return 'Test Taker';
    }
    if (name.startsWith('second_camera')) {
      return 'Second Camera';
    }

    return name;
  };

  /**
   * Format all diagnostic data as markdown for clipboard copy
   * This allows proctors to quickly paste diagnostic info into support tickets
   */
  const formatDiagnosticsAsMarkdown = useCallback(() => {
    if (!diagnostics.room) {
      return '# Session Diagnostics\n\nNo LiveKit room connection available';
    }

    let markdown = '# Session Diagnostics\n\n';
    markdown += `**Generated:** ${new Date().toLocaleString()}\n\n`;

    // User Information
    markdown += '## User Information\n\n';
    markdown += `- **User SID:** ${user?.sid || 'N/A'}\n`;
    markdown += `- **User Name:** ${user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'N/A'}\n`;
    markdown += `- **User Email:** ${user?.email || 'N/A'}\n\n`;

    // Proctor Room Information
    markdown += '## Proctor Room  Information\n\n';
    markdown += `- **Proctor Room SID:** ${attachedToProctorRoomSid || 'N/A'}\n\n`;

    // Room Information
    markdown += '## Media Room Information\n\n';

    markdown += `- **Media Room Name:** ${diagnostics.room.name || 'N/A'}\n`;
    markdown += `- **LiveKit Room ID:** ${diagnostics.room.sid || 'N/A'}\n`;
    markdown += `- **Connection State:** ${formatConnectionState(diagnostics.connectionState)}\n`;
    markdown += `- **Recording:** ${diagnostics.isRecording ? 'Recording' : 'Not Recording'}\n`;
    markdown += `- **Participants:** ${diagnostics.room.numParticipants || 0}\n`;
    markdown += `- **Publishers:** ${diagnostics.room.numPublishers || 0}\n`;
    markdown += `- **Departure Timeout:** ${diagnostics.room.departureTimeout ? `${diagnostics.room.departureTimeout} seconds` : 'N/A'}\n`;
    markdown += `- **Empty Timeout:** ${diagnostics.room.emptyTimeout ? `${diagnostics.room.emptyTimeout} seconds` : 'N/A'}\n\n`;

    // Local Participant
    if (diagnostics.localParticipant) {
      markdown += '## Local Participant\n\n';
      markdown += `- **Identity:** ${diagnostics.localParticipant.identity}\n`;
      markdown += `- **SID:** ${diagnostics.localParticipant.sid}\n`;
      markdown += `- **Published Tracks:** ${diagnostics.localParticipant.tracks.length}\n\n`;

      if (diagnostics.localParticipant.tracks.length > 0) {
        markdown += '### Local Tracks\n\n';
        diagnostics.localParticipant.tracks.forEach((track, idx) => {
          markdown += `${idx + 1}. **${track.kind}** (${track.source})\n`;
          markdown += `   - Track: ${track.trackName || track.trackSid}\n`;
          markdown += `   - Status: ${track.muted ? 'Muted' : 'Active'}\n`;

          const stats = trackStats[track.trackSid];
          if (stats) {
            markdown += `   - **Statistics:**\n`;
            if (stats.bitrate > 0) markdown += `     - Bitrate: ${formatBitrate(stats.bitrate)}\n`;
            if (stats.framesPerSecond > 0) markdown += `     - FPS: ${stats.framesPerSecond.toFixed(1)}\n`;
            if (stats.frameWidth > 0 && stats.frameHeight > 0) markdown += `     - Resolution: ${stats.frameWidth}x${stats.frameHeight}\n`;
            if (stats.bytesReceived > 0) markdown += `     - Bytes Received: ${formatBytes(stats.bytesReceived)}\n`;
            if (stats.bytesSent > 0) markdown += `     - Bytes Sent: ${formatBytes(stats.bytesSent)}\n`;
            const loss = formatPacketLoss(stats.packetsLost, stats.packetsReceived);
            if (loss.count > 0) markdown += `     - Packet Loss: ${loss.count} (${loss.percent}%)\n`;
            if (stats.framesDropped > 0) markdown += `     - Frames Dropped: ${stats.framesDropped}\n`;
          }
          markdown += '\n';
        });
      }
    }

    // Remote Participants
    markdown += `## Remote Participants (${diagnostics.participants.length})\n\n`;
    if (diagnostics.participants.length === 0) {
      markdown += 'No remote participants\n\n';
    } else {
      diagnostics.participants.forEach((participant, idx) => {
        markdown += `### ${idx + 1}. ${getParticipantDisplayName(participant.name)}\n\n`;
        markdown += `- **Identity:** ${participant.identity}\n`;
        markdown += `- **SID:** ${participant.sid}\n`;
        markdown += `- **Connection Quality:** ${formatConnectionQuality(participant.connectionQuality)}\n`;
        if (participant.isSpeaking) markdown += `- **Status:** Speaking\n`;
        markdown += `- **Tracks:** ${participant.tracks.length}\n\n`;

        if (participant.tracks.length > 0) {
          markdown += '**Track Details:**\n\n';
          participant.tracks.forEach((track, trackIdx) => {
            markdown += `${trackIdx + 1}. **${track.kind}** (${track.source})\n`;
            markdown += `   - Track: ${track.trackName || track.trackSid}\n`;
            markdown += `   - Subscribed: ${track.subscribed ? 'Yes' : 'No'}\n`;
            markdown += `   - Muted: ${track.muted ? 'Yes' : 'No'}\n`;
            if (track.dimensions) {
              markdown += `   - Published Dimensions: ${track.dimensions.width}x${track.dimensions.height}\n`;
            }
            if (track.kind === 'video') {
              markdown += `   - Simulcasted: ${track.simulcasted ? 'Yes' : 'No'}\n`;
              if (track.layers && track.layers.length > 0) {
                const sortedLayers = [...track.layers].sort((a, b) => (a.width * a.height) - (b.width * b.height));
                markdown += `   - Published Layers: ${sortedLayers.map(l => `${l.width}x${l.height}`).join(', ')}\n`;
              }
              if (track.videoQuality !== undefined) {
                markdown += `   - Consuming Layer: ${formatVideoQuality(track.videoQuality)}\n`;
              }
            }

            const stats = trackStats[track.trackSid];
            if (stats) {
              markdown += `   - **Statistics:**\n`;
              if (stats.bitrate > 0) markdown += `     - Bitrate: ${formatBitrate(stats.bitrate)}\n`;
              if (stats.framesPerSecond > 0) markdown += `     - FPS: ${stats.framesPerSecond.toFixed(1)}\n`;
              if (stats.frameWidth > 0 && stats.frameHeight > 0) markdown += `     - Receiving Resolution: ${stats.frameWidth}x${stats.frameHeight}\n`;
              if (stats.bytesReceived > 0) markdown += `     - Bytes Received: ${formatBytes(stats.bytesReceived)}\n`;
              if (stats.bytesSent > 0) markdown += `     - Bytes Sent: ${formatBytes(stats.bytesSent)}\n`;
              const loss = formatPacketLoss(stats.packetsLost, stats.packetsReceived);
              if (loss.count > 0) markdown += `     - Packet Loss: ${loss.count} (${loss.percent}%)\n`;
              if (stats.framesDropped > 0) markdown += `     - Frames Dropped: ${stats.framesDropped}\n`;
            }
            markdown += '\n';
          });
        }
      });
    }

    return markdown;
  }, [diagnostics, trackStats, user, attachedToProctorRoomSid, roomId]);

  /**
   * Copy diagnostic data to clipboard in markdown format
   */
  const handleCopyToClipboard = useCallback(async () => {
    try {
      const markdown = formatDiagnosticsAsMarkdown();
      await navigator.clipboard.writeText(markdown);
      setCopySuccess(true);

      // Reset success message after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy diagnostics to clipboard:', error);
    }
  }, [formatDiagnosticsAsMarkdown]);

  const renderTrackStats = (trackSid) => {
    const stats = trackStats[trackSid];
    if (!stats) {
      return (
        <Text size="x-small" color="secondary">No statistics available</Text>
      );
    }

    return (
      <View as="div" padding="x-small" background="secondary" borderRadius="small">
        <Flex direction="column" gap="xx-small">
          {stats.bitrate > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Bitrate:</Text>
              <Text size="x-small">{formatBitrate(stats.bitrate)}</Text>
            </Flex>
          )}
          {stats.framesPerSecond > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">FPS:</Text>
              <Text size="x-small">{stats.framesPerSecond.toFixed(1)}</Text>
            </Flex>
          )}
          {(stats.frameWidth > 0 && stats.frameHeight > 0) && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Resolution:</Text>
              <Text size="x-small">{stats.frameWidth}x{stats.frameHeight}</Text>
            </Flex>
          )}
          {stats.bytesReceived > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Bytes Received:</Text>
              <Text size="x-small">{formatBytes(stats.bytesReceived)}</Text>
            </Flex>
          )}
          {stats.bytesSent > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Bytes Sent:</Text>
              <Text size="x-small">{formatBytes(stats.bytesSent)}</Text>
            </Flex>
          )}
          {(stats.packetsLost > 0 || stats.packetsReceived > 0) && (() => {
            const loss = formatPacketLoss(stats.packetsLost, stats.packetsReceived);
            return (
              <Flex justifyItems="space-between">
                <Text size="x-small" variant="contentImportant">Packet Loss:</Text>
                <Text size="x-small" color={loss.count > 0 ? 'danger' : undefined}>
                  {loss.count} ({loss.percent}%)
                </Text>
              </Flex>
            );
          })()}
          {stats.framesDropped > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Frames Dropped:</Text>
              <Text size="x-small" color="danger">{stats.framesDropped}</Text>
            </Flex>
          )}
          {stats.jitter > 0 && (
            <Flex justifyItems="space-between">
              <Text size="x-small" variant="contentImportant">Jitter:</Text>
              <Text size="x-small">{(stats.jitter * 1000).toFixed(2)} ms</Text>
            </Flex>
          )}
        </Flex>
      </View>
    );
  };

  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      initialPosition="top left"
      title="Session Diagnostics"
      initialSize={{ width: 700, height: 800 }}
      minSize={{ width: 500, height: 400 }}
      resizable={true}
      minimizable={true}
      constrainToContainer={false}
      maintainPositionOnClose={true}
    >
      <View as="div" padding="small" style={{ height: '100%', overflowY: 'auto' }}>
        {!room ? (
          <View as="div" textAlign="center" padding="large">
            <Text variant="contentImportant">No LiveKit room connection available</Text>
          </View>
        ) : (
          <Flex direction="column" gap="small">
            {/* Copy to Clipboard Button */}
            <View as="div" textAlign="start" margin="none none small">
              <Button
                onClick={handleCopyToClipboard}
                color={copySuccess ? 'success' : 'primary'}
                size="small"
                renderIcon={IconCopyLine}
              >
                {copySuccess ? 'Copied!' : 'Copy Diagnostics to Clipboard'}
              </Button>
            </View>
            {/* Room Information */}
            <View as="div" background="secondary" padding="small" borderRadius="medium">
              <Heading level="h3" margin="none none small">Media Room Information</Heading>
              <Flex direction="column" gap="x-small">
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">Media Room Name:</Text>
                  <Text>{diagnostics.room?.name || 'N/A'}</Text>
                </Flex>
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">LiveKit Room ID:</Text>
                  <Text size="small">{diagnostics.room?.sid || 'N/A'}</Text>
                </Flex>
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">Connection State:</Text>
                  <View
                    as="span"
                    padding="xx-small small"
                    borderRadius="medium"
                    background="primary"
                    style={{ color: getConnectionStateColor(diagnostics.connectionState), fontWeight: 'bold' }}
                  >
                    <Text size="small">{formatConnectionState(diagnostics.connectionState)}</Text>
                  </View>
                </Flex>
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">Recording:</Text>
                  <View
                    as="span"
                    padding="xx-small small"
                    borderRadius="medium"
                    background="primary"
                    style={{ color: diagnostics.isRecording ? '#D01A19' : '#394B59', fontWeight: 'bold' }}
                  >
                    <Text size="small">{diagnostics.isRecording ? 'Recording' : 'Not Recording'}</Text>
                  </View>
                </Flex>
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">Participants:</Text>
                  <Text>{diagnostics.room?.numParticipants || 0}</Text>
                </Flex>
                <Flex justifyItems="space-between">
                  <Text variant="contentImportant">Publishers:</Text>
                  <Text>{diagnostics.room?.numPublishers || 0}</Text>
                </Flex>
                {diagnostics.lastUpdated && (
                  <Flex justifyItems="space-between">
                    <Text variant="contentImportant">Last Updated:</Text>
                    <Text size="small">{diagnostics.lastUpdated.toLocaleTimeString()}</Text>
                  </Flex>
                )}
              </Flex>
            </View>

            {/* Local Participant */}
            {diagnostics.localParticipant && (
              <View as="div" background="secondary" padding="small" borderRadius="medium">
                <Heading level="h3" margin="none none small">Local Participant</Heading>
                <Flex direction="column" gap="x-small">
                  <Flex justifyItems="space-between">
                    <Text variant="contentImportant">Identity:</Text>
                    <Text>{diagnostics.localParticipant.identity}</Text>
                  </Flex>
                  <Flex justifyItems="space-between">
                    <Text variant="contentImportant">SID:</Text>
                    <Text size="small">{diagnostics.localParticipant.sid}</Text>
                  </Flex>
                  <View as="div" margin="small none none">
                    <Text variant="contentImportant">Published Tracks ({diagnostics.localParticipant.tracks.length}):</Text>
                    {diagnostics.localParticipant.tracks.length === 0 ? (
                      <Text size="small" margin="x-small none none">No tracks published</Text>
                    ) : (
                      <View as="div" margin="x-small none none">
                        {diagnostics.localParticipant.tracks.map((track, idx) => (
                          <View key={idx} as="div" padding="x-small" margin="x-small none" background="primary" borderRadius="small">
                            <Flex direction="column" gap="xx-small">
                              <Flex justifyItems="space-between">
                                <Text size="small" variant="contentImportant">{track.kind}</Text>
                                <View
                                  as="span"
                                  padding="xx-small small"
                                  borderRadius="medium"
                                  background="secondary"
                                  style={{ color: track.muted ? '#394B59' : '#0B874B', fontWeight: 'bold' }}
                                >
                                  <Text size="x-small">{track.muted ? 'Muted' : 'Active'}</Text>
                                </View>
                              </Flex>
                              <Text size="x-small">Source: {track.source}</Text>
                              <Text size="x-small">Track: {track.trackName || track.trackSid}</Text>
                              <ToggleDetails
                                summary={<Text size="x-small" variant="contentImportant">Stream Statistics</Text>}
                                size="small"
                              >
                                {renderTrackStats(track.trackSid)}
                              </ToggleDetails>
                            </Flex>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                </Flex>
              </View>
            )}

            {/* Remote Participants */}
            <View as="div" background="secondary" padding="small" borderRadius="medium">
              <Heading level="h3" margin="none none small">
                Remote Participants ({diagnostics.participants.length})
              </Heading>
              {diagnostics.participants.length === 0 ? (
                <Text>No remote participants</Text>
              ) : (
                <Flex direction="column" gap="small">
                  {diagnostics.participants.map((participant, idx) => (
                    <View key={idx} as="div" background="primary" padding="small" borderRadius="small">
                      <Flex direction="column" gap="x-small">
                        <Flex justifyItems="space-between" alignItems="center">
                          <Text variant="contentImportant">{getParticipantDisplayName(participant.name)}</Text>
                          <Flex alignItems="center" gap="xx-small">
                            <Tooltip
                              renderTip="Connection Quality: Indicates the network connection strength between this participant and the media server"
                              placement="top"
                            >
                              <IconInfoLine size="x-small" color="secondary" />
                            </Tooltip>
                            <View
                              as="span"
                              padding="xx-small small"
                              borderRadius="medium"
                              background="secondary"
                              style={{ color: getConnectionQualityColor(participant.connectionQuality), fontWeight: 'bold' }}
                            >
                              <Text size="x-small">{formatConnectionQuality(participant.connectionQuality)}</Text>
                            </View>
                          </Flex>
                        </Flex>
                        <Text size="small">Identity: {participant.identity}</Text>
                        <Text size="x-small">SID: {participant.sid}</Text>
                        {participant.isSpeaking && (
                          <View
                            as="span"
                            padding="xx-small small"
                            borderRadius="medium"
                            background="secondary"
                            style={{ color: '#0770A3', fontWeight: 'bold' }}
                          >
                            <Text size="x-small">Speaking</Text>
                          </View>
                        )}
                        <View as="div" margin="small none none">
                          <Text size="small" variant="contentImportant">Tracks ({participant.tracks.length}):</Text>
                          {participant.tracks.length === 0 ? (
                            <Text size="x-small" margin="x-small none none">No tracks</Text>
                          ) : (
                            <View as="div" margin="x-small none none">
                              {participant.tracks.map((track, trackIdx) => (
                                <View key={trackIdx} as="div" padding="x-small" margin="x-small none" background="secondary" borderRadius="small">
                                  <Flex direction="column" gap="xx-small">
                                    <Flex justifyItems="space-between" alignItems="center">
                                      <Text size="x-small" variant="contentImportant">{track.kind} - {track.source}</Text>
                                      <Flex gap="xx-small" alignItems="center">
                                        <Tooltip
                                          renderTip="Subscribed: You are receiving this track's media data. Muted: The track is not sending audio/video data."
                                          placement="top"
                                        >
                                          <IconInfoLine size="x-small" color="secondary" />
                                        </Tooltip>
                                        {track.subscribed && (
                                          <View
                                            as="span"
                                            padding="xx-small x-small"
                                            borderRadius="medium"
                                            background="primary"
                                            style={{ color: '#0B874B', fontWeight: 'bold' }}
                                          >
                                            <Text size="x-small">Subscribed</Text>
                                          </View>
                                        )}
                                        {track.muted && (
                                          <View
                                            as="span"
                                            padding="xx-small x-small"
                                            borderRadius="medium"
                                            background="primary"
                                            style={{ color: '#394B59', fontWeight: 'bold' }}
                                          >
                                            <Text size="x-small">Muted</Text>
                                          </View>
                                        )}
                                      </Flex>
                                    </Flex>
                                    <Text size="x-small">Track: {track.trackName || track.trackSid}</Text>
                                    {track.dimensions && (
                                      <Text size="x-small">
                                        Published: {track.dimensions.width}x{track.dimensions.height}
                                      </Text>
                                    )}
                                    {track.kind === 'video' && (
                                      <Flex gap="xx-small" alignItems="center" wrap="wrap">
                                        <Text size="x-small" variant="contentImportant">Layers:</Text>
                                        {track.layers && track.layers.length > 1 ? (
                                          <Flex gap="xx-small" alignItems="center">
                                            {track.layers
                                              .sort((a, b) => (a.width * a.height) - (b.width * b.height))
                                              .map((layer, idx) => {
                                                const stats = trackStats[track.trackSid];
                                                const isCurrentLayer = stats && stats.frameWidth > 0 &&
                                                  stats.frameWidth === layer.width && stats.frameHeight === layer.height;
                                                const qualityLabels = { [VideoQuality.LOW]: 'Low', [VideoQuality.MEDIUM]: 'Medium', [VideoQuality.HIGH]: 'High' };
                                                const qualityLabel = qualityLabels[layer.quality] || `L${idx}`;
                                                return (
                                                  <Tooltip
                                                    key={idx}
                                                    renderTip={`${qualityLabel} layer: ${layer.width}x${layer.height}${layer.bitrate ? ` (${formatBitrate(layer.bitrate)})` : ''}${isCurrentLayer ? ' — Currently receiving' : ''}`}
                                                    placement="top"
                                                  >
                                                    <View
                                                      as="span"
                                                      padding="xx-small x-small"
                                                      borderRadius="medium"
                                                      background="primary"
                                                      style={{
                                                        color: isCurrentLayer ? getVideoQualityColor(layer.quality) : '#8B969E',
                                                        fontWeight: isCurrentLayer ? 'bold' : 'normal',
                                                        border: isCurrentLayer ? `2px solid ${getVideoQualityColor(layer.quality)}` : '1px solid #D7DEE2',
                                                      }}
                                                    >
                                                      <Text size="x-small">{layer.width}x{layer.height}</Text>
                                                    </View>
                                                  </Tooltip>
                                                );
                                              })}
                                          </Flex>
                                        ) : track.layers && track.layers.length === 1 ? (
                                          <Text size="x-small" color="secondary">Single layer ({track.layers[0].width}x{track.layers[0].height})</Text>
                                        ) : (
                                          <Text size="x-small" color="secondary">{track.simulcasted ? 'Simulcast (layers not reported)' : 'Single layer'}</Text>
                                        )}
                                      </Flex>
                                    )}
                                    {track.kind === 'video' && trackStats[track.trackSid] && trackStats[track.trackSid].frameWidth > 0 && (
                                      <Flex justifyItems="space-between">
                                        <Text size="x-small" variant="contentImportant">Receiving:</Text>
                                        <Text size="x-small">
                                          {trackStats[track.trackSid].frameWidth}x{trackStats[track.trackSid].frameHeight}
                                          {trackStats[track.trackSid].framesPerSecond > 0 && ` @ ${trackStats[track.trackSid].framesPerSecond.toFixed(0)}fps`}
                                        </Text>
                                      </Flex>
                                    )}
                                    <ToggleDetails
                                      summary={<Text size="x-small" variant="contentImportant">Stream Statistics</Text>}
                                      size="small"
                                    >
                                      {renderTrackStats(track.trackSid)}
                                    </ToggleDetails>
                                  </Flex>
                                </View>
                              ))}
                            </View>
                          )}
                        </View>
                      </Flex>
                    </View>
                  ))}
                </Flex>
              )}
            </View>

            {/* All Published Tracks Summary */}
            <View as="div" background="secondary" padding="small" borderRadius="medium">
              <Heading level="h3" margin="none none small">
                All Published Tracks ({diagnostics.tracks.length})
              </Heading>
              {diagnostics.tracks.length === 0 ? (
                <Text>No tracks published</Text>
              ) : (
                <View as="div">
                  {Object.entries(
                    diagnostics.tracks.reduce((acc, track) => {
                      if (!acc[track.participantIdentity]) {
                        acc[track.participantIdentity] = [];
                      }
                      acc[track.participantIdentity].push(track);
                      return acc;
                    }, {})
                  ).map(([identity, tracks]) => (
                    <View key={identity} as="div" margin="none none small">
                      <Text size="small" variant="contentImportant" margin="none none x-small">
                        {getParticipantDisplayName(identity)} ({tracks.length} {tracks.length === 1 ? 'track' : 'tracks'})
                      </Text>
                      <View as="div" margin="x-small none none">
                        {tracks.map((track, idx) => (
                          <View key={idx} as="div" padding="x-small" margin="x-small none" background="primary" borderRadius="small">
                            <Flex direction="column" gap="xx-small">
                              <Flex justifyItems="space-between" alignItems="center">
                                <Flex direction="column" gap="xx-small">
                                  <Text size="small" variant="contentImportant">
                                    {track.kind}
                                  </Text>
                                  <Text size="x-small">Source: {track.source}</Text>
                                  <Text size="x-small">Track: {track.trackName || track.trackSid}</Text>
                                </Flex>
                                <Flex direction="column" gap="xx-small" alignItems="end">
                                  <Flex gap="xx-small" alignItems="center">
                                    <Tooltip
                                      renderTip="Subscribed: You are receiving this track's media data. Muted: The track is not sending audio/video data."
                                      placement="top"
                                    >
                                      <IconInfoLine size="x-small" color="secondary" />
                                    </Tooltip>
                                    <Flex direction="column" gap="xx-small" alignItems="end">
                                      {track.subscribed && (
                                        <View
                                          as="span"
                                          padding="xx-small x-small"
                                          borderRadius="medium"
                                          background="secondary"
                                          style={{ color: '#0B874B', fontWeight: 'bold' }}
                                        >
                                          <Text size="x-small">Subscribed</Text>
                                        </View>
                                      )}
                                      {track.muted && (
                                        <View
                                          as="span"
                                          padding="xx-small x-small"
                                          borderRadius="medium"
                                          background="secondary"
                                          style={{ color: '#394B59', fontWeight: 'bold' }}
                                        >
                                          <Text size="x-small">Muted</Text>
                                        </View>
                                      )}
                                    </Flex>
                                  </Flex>
                                </Flex>
                              </Flex>
                              <ToggleDetails
                                summary={<Text size="x-small" variant="contentImportant">Stream Statistics</Text>}
                                size="small"
                              >
                                {renderTrackStats(track.trackSid)}
                              </ToggleDetails>
                            </Flex>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </Flex>
        )}
      </View>
    </DraggableModal>
  );
};

export default SessionDiagnostics;
